/**
 * Carga a demanda de Google Maps JavaScript (especificación de mapas, § 5.2:
 * «en web, cargar de forma diferida la integración que corresponda»).
 *
 * El script se pide una sola vez por página, solo cuando alguien abre la
 * pestaña Rastreo y el servidor dijo que el proveedor es Google. No se carga
 * un segundo proveedor «por si acaso».
 *
 * Solo se tipa lo que el adaptador usa: así no entra una dependencia de tipos
 * entera para una docena de llamadas, y si Google cambia algo de esto el error
 * aparece aquí y no en la pantalla.
 */

export type LatLngLiteral = { lat: number; lng: number };

export type GoogleMapsListener = { remove(): void };

export interface GoogleMap {
  fitBounds(bounds: GoogleLatLngBounds, padding?: number): void;
  setCenter(center: LatLngLiteral): void;
  panTo(center: LatLngLiteral): void;
  setZoom(zoom: number): void;
  getCenter(): { lat(): number; lng(): number } | undefined;
  getZoom(): number | undefined;
}

export interface GoogleMarker {
  setPosition(position: LatLngLiteral): void;
  setIcon(icon: GoogleSymbol): void;
  setLabel(label: GoogleMarkerLabel): void;
  setTitle(title: string): void;
  setZIndex(zIndex: number): void;
  setMap(map: GoogleMap | null): void;
  addListener(event: "click", handler: () => void): GoogleMapsListener;
}

export interface GooglePolyline {
  setPath(path: LatLngLiteral[]): void;
  setMap(map: GoogleMap | null): void;
}

export interface GoogleLatLngBounds {
  extend(point: LatLngLiteral): void;
  isEmpty(): boolean;
}

export type GoogleSymbol = {
  path: number;
  scale: number;
  fillColor: string;
  fillOpacity: number;
  strokeColor: string;
  strokeWeight: number;
  /** Dónde va la etiqueta, en coordenadas del símbolo. Google exige un `Point`, no un literal. */
  labelOrigin?: unknown;
};

/** Etiqueta de texto de un marcador clásico. No exige `map ID`. */
export type GoogleMarkerLabel = {
  text: string;
  color: string;
  fontSize: string;
  fontWeight: string;
  className?: string;
};

export interface GoogleMapsNamespace {
  Map: new (container: HTMLElement, options: Record<string, unknown>) => GoogleMap;
  Marker: new (options: Record<string, unknown>) => GoogleMarker;
  Polyline: new (options: Record<string, unknown>) => GooglePolyline;
  LatLngBounds: new () => GoogleLatLngBounds;
  /** Opcional en el tipo: si faltara, el marcador sale sin desplazar la etiqueta, no se rompe. */
  Point?: new (x: number, y: number) => unknown;
  SymbolPath: { CIRCLE: number };
  event: { clearInstanceListeners(instance: unknown): void };
}

/** Plazo de carga inicial propuesto por la especificación (§ 6, `initialLoadTimeoutMs`). */
export const GOOGLE_LOAD_TIMEOUT_MS = 12_000;

const CALLBACK = "__guardiaGoogleMapsListo";

type MapsWindow = Window & {
  google?: { maps?: unknown };
  [CALLBACK]?: () => void;
  /** Google llama a esta función global si la clave es rechazada. */
  gm_authFailure?: () => void;
};

let pending: Promise<GoogleMapsNamespace> | null = null;
let authFailureListeners: Array<() => void> = [];

function readNamespace(win: MapsWindow): GoogleMapsNamespace | null {
  const maps = win.google?.maps as Partial<GoogleMapsNamespace> | undefined;
  if (!maps || typeof maps.Map !== "function" || typeof maps.Marker !== "function") return null;
  if (typeof maps.Polyline !== "function" || typeof maps.LatLngBounds !== "function") return null;
  return maps as GoogleMapsNamespace;
}

/**
 * Se suscribe al fallo de autenticación de la clave (clave inválida, sin
 * facturación o dominio no autorizado). Google no lo reporta como error del
 * script: avisa por `gm_authFailure` después de cargar.
 */
export function onGoogleAuthFailure(listener: () => void): () => void {
  authFailureListeners.push(listener);
  return () => {
    authFailureListeners = authFailureListeners.filter((item) => item !== listener);
  };
}

export function loadGoogleMaps(apiKey: string, win: MapsWindow = window as MapsWindow): Promise<GoogleMapsNamespace> {
  const ready = readNamespace(win);
  if (ready) return Promise.resolve(ready);
  if (pending) return pending;

  pending = new Promise<GoogleMapsNamespace>((resolve, reject) => {
    const timer = win.setTimeout(() => {
      pending = null;
      reject(new Error("Google Maps no terminó de cargar a tiempo."));
    }, GOOGLE_LOAD_TIMEOUT_MS);

    win.gm_authFailure = () => {
      for (const listener of authFailureListeners) listener();
    };

    win[CALLBACK] = () => {
      win.clearTimeout(timer);
      const namespace = readNamespace(win);
      if (namespace) resolve(namespace);
      else {
        pending = null;
        reject(new Error("Google Maps cargó sin las piezas que usa Drive."));
      }
    };

    const params = new URLSearchParams({ key: apiKey, v: "weekly", callback: CALLBACK, language: "es", region: "CO" });
    const script = win.document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.onerror = () => {
      win.clearTimeout(timer);
      pending = null;
      script.remove();
      reject(new Error("No se pudo descargar Google Maps."));
    };
    win.document.head.appendChild(script);
  });
  return pending;
}

/** Solo para pruebas: olvida la carga en curso. */
export function resetGoogleMapsLoaderForTests() {
  pending = null;
  authFailureListeners = [];
}
